import React, { PropsWithChildren, useEffect } from 'react';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { store, persistor } from './store';
import { PersistGate } from 'redux-persist/integration/react';
import { HashRouter as Router, Switch, Redirect, useHistory } from 'react-router-dom';
import 'moment/locale/es';
import { Route, Modal, User } from 'components';
import { RootState } from 'reducers';
import { Socket, Constants, Globals, SocketEvents } from 'utils';
import { performLogout } from 'utils/logout';
import { AuthService, RevisionService, PreparationService } from 'services';
import * as Jetemit from 'jetemit';

import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';

// Public
import Home from 'screens/home';
import RedirectMemberships from 'screens/redirect-memberships';

// Auth
import Login from 'screens/auth/login/login';
import Recover from 'screens/auth/login/recover';
import Register from 'screens/auth/register/register';
import Reset from 'screens/auth/login/reset';
import Profile from 'screens/profile';

// Admin
import Admin from 'screens/admin/home';
import AdminHome from 'screens/admin/home/home';
import AdminModerators from 'screens/admin/users/moderators/moderators';
import AdminLawyers from 'screens/admin/users/lawyers/lawyers';
import AdminClients from 'screens/admin/users/clients/clients';
import AdminUsers from 'screens/admin/users/users';
import AdminServices from 'screens/admin/services/services';
import AdminReports from 'screens/admin/reports/reports';
import AdminReportPayments from 'screens/admin/reports/payments';
import AdminReportServicePayments from 'screens/admin/reports/service-payments';
import AdminReportComissions from 'screens/admin/reports/comissions';
import AdminReportPackagePayments from 'screens/admin/reports/package-payments';
import AdminGuides from 'screens/admin/guides/guides';
import AdminCalls from 'screens/admin/calls/calls';
import AdminChat from 'screens/admin/chat/chat';
import AdminNotifications from 'screens/admin/notifications/notifications';
import AdminFormalities from 'screens/admin/formalities/formalities';
import AdminMemberships from 'screens/admin/memberships/memberships';
import AdminRevision from 'screens/admin/revision/revision';
import AdminSignature from 'screens/admin/signature/signature';
import AdminRatings from 'screens/admin/ratings/ratings';
import AdminDocuments from 'screens/admin/documents/documents';
import AdminPreparation from 'screens/admin/preparation/preparation';
import AdminAnalytics from 'screens/admin/analytics/analytics';
import AdminReferrals from 'screens/admin/referrals/referrals';

// Client
import Client from 'screens/client/home';
import ClientHome from 'screens/client/home/home';
import ClientBotpressChat from 'screens/client/botpress-chat/botpress-chat';
import ClientChat from 'screens/client/chat/chat';
import ClientCalls from 'screens/client/calls/calls';
import ClientFormalities from 'screens/client/formalities/formalities';
import ClientMemberships from 'screens/client/memberships/memberships';
import ClientSignatures from 'screens/client/signatures/signatures';
import ClientRevision from 'screens/client/revision/revision';
import ClientReports from 'screens/client/reports/reports';
import ClientSurveys from 'screens/client/surveys/surveys';
import ClientPreparation from 'screens/client/preparation/preparation';
import ClientGuides from 'screens/client/guides/guides';
import ClientReferrals from 'screens/client/referrals/referrals';

// Lawyer
import Lawyer from 'screens/lawyer/home';
import LawyerHome from 'screens/lawyer/home/home';
import LawyerValidation from 'screens/lawyer/home/validation';
import LawyerCalls from 'screens/lawyer/calls/calls';
import LawyerSignatures from 'screens/lawyer/signatures/signatures';
import LawyerRevision from 'screens/lawyer/revision/revision';
import LawyerServices from 'screens/lawyer/services/services';
import LawyerChat from 'screens/lawyer/chat/chat';
import LawyerPreparation from 'screens/lawyer/preparation/preparation';
import LawyerGuides from 'screens/lawyer/guides/guides';

// Scripts
import 'jquery/dist/jquery.min.js';
import 'bootstrap/dist/js/bootstrap.min.js';

// Styles
import 'bootstrap/dist/css/bootstrap.min.css';
import 'font-awesome/css/font-awesome.min.css';
import './scss/main.scss';
import chat from 'screens/lawyer/chat/chat';

import Lottie from 'lottie-react';
import logo from './assets/LogoLegalmente.svg';
import requestCert from 'screens/apacuana/requestCert/requestCert';
import { ApacuanaProvider } from 'contexts/ApacuanaContext';

const Container = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  // Estado global para el modal de aceptar revisión documental.
  // Vive aquí para que el abogado reciba la notificación desde cualquier pantalla,
  // no solo cuando está en /lawyer/revision.
  const [visibleAcceptRevision, setVisibleAcceptRevision] = React.useState(false);
  const [pendingRevision, setPendingRevision] = React.useState<any>(null);

  // Estado global para el modal de aceptar elaboración documental.
  // Mismo patrón que revisión: vive aquí para funcionar desde cualquier pantalla.
  const [visibleAcceptPreparation, setVisibleAcceptPreparation] = React.useState(false);
  const [pendingSearchPreparation, setPendingSearchPreparation] = React.useState<any>(null);

  // DEV-LT-35: estado global para la POSTULACIÓN a Revisión Documental (abogado
  // externo). Distinto del par visibleAcceptRevision/pendingRevision de arriba, que
  // es exclusivo del flujo interno (asignación directa sobre una DocumentalRevision
  // ya creada). Aquí aún no existe esa revisión -- solo un SearchRevision, y el
  // cliente decide después con quién continuar. Mirror exacto del par de
  // visibleAcceptPreparation/pendingSearchPreparation.
  const [visibleAcceptSearchRevision, setVisibleAcceptSearchRevision] = React.useState(false);
  const [pendingSearchRevision, setPendingSearchRevision] = React.useState<any>(null);

  const acceptPreparationGlobal = async () => {
    try {
      const currentUser = store.getState().user;
      await PreparationService.acceptPreparation({
        search_preparation_id: pendingSearchPreparation?.id,
        lawyer_id: currentUser?.id,
        withoutLoading: true,
      });
      Globals.showSuccess('Te has postulado a la elaboración correctamente');
      setVisibleAcceptPreparation(false);
      setPendingSearchPreparation(null);
      // Notifica a LawyerPreparation (si está montado) que recargue la lista.
      Jetemit.emit('preparation/reload', {});
    } catch (error) {}
  };

  const acceptRevisionGlobal = async () => {
    try {
      const currentUser = store.getState().user;
      await RevisionService.acceptRevision({
        user_id: currentUser?.id,
        revision_id: pendingRevision?.id,
        withoutLoading: true,
      });
      Globals.showSuccess('Se ha aceptado la revisión correctamente');
      setVisibleAcceptRevision(false);
      setPendingRevision(null);
      // Notifica a LawyerRevision (si está montado) que recargue la lista.
      Jetemit.emit('revision/reload', {});
    } catch (error) {}
  };

  // DEV-LT-35: POSTULARSE a una búsqueda de Revisión (abogado externo). A diferencia
  // de acceptRevisionGlobal, esto NO asigna el caso -- solo registra el interés del
  // abogado (SearchRevisionAttendance); el cliente elige después de la lista de
  // postulados. Mirror exacto de acceptPreparationGlobal.
  const acceptSearchRevisionGlobal = async () => {
    try {
      const currentUser = store.getState().user;
      await RevisionService.acceptSearchRevision({
        search_revision_id: pendingSearchRevision?.id,
        lawyer_id: currentUser?.id,
        withoutLoading: true,
      });
      Globals.showSuccess('Te has postulado la revisión correctamente');
      setVisibleAcceptSearchRevision(false);
      setPendingSearchRevision(null);
      Jetemit.emit('revision/reload', {});
    } catch (error) {}
  };

  useEffect(() => {
    const getUser = async () => {
      const user = store.getState().user;

      if (user) {
        const res: any = await AuthService.checkUser({
          user_id: user?.id,
          withoutLoading: true,
        });
        if (res.user) {
          if (res.user.status == Constants.USER_STATUS.ACTIVE) {
            dispatch({
              type: 'SET_USER',
              payload: res.user,
            });
          } else {
            Globals.showError('Lo sentimos, su usuario ha sido suspendido');
            close();
          }
        } else {
          Globals.showError('Lo sentimos, su usuario ha sido eliminado');
          close();
        }
      }
    };
    getUser();

    const close = async () => {
      await performLogout();
      history.replace('/logout');
    };

    Socket.on(SocketEvents.USER_UPDATE, (data: any) => {
      const user = store.getState().user;
      if (user != null && user?.id == data.user_id) {
        getUser();
      }
    });

    Socket.on(SocketEvents.CHATS.MESSAGE, (data: any) => {
      Jetemit.emit(SocketEvents.CHATS.MESSAGE, data);
    });

    /* Nueva solicitud de llamada telefónica: notifica al abogado asignado sin
       que tenga que recargar el panel de Llamadas. */
    Socket.on(SocketEvents.CALLS.NEW_CALL, (data: any) => {
      Jetemit.emit(SocketEvents.CALLS.NEW_CALL, data);
    });

    /* Cambio de estado de una llamada (resuelta / sin resolver / en revisión):
       notifica al cliente sin que tenga que recargar. */
    Socket.on(SocketEvents.CALLS.STATUS_CHANGE, (data: any) => {
      Jetemit.emit(SocketEvents.CALLS.STATUS_CHANGE, data);
    });

    // Permite que ModalComments se refresque en tiempo real sin recargar la Página
    Socket.on(SocketEvents.COMMENTS.MESSAGE, (data: any) => {
      Jetemit.emit(SocketEvents.COMMENTS.MESSAGE, data);
    });

    /* Socket para el cliente para mostrar los abogados en elaboracion de documentos */
    Socket.on(SocketEvents.SEARCH_PREPARATION.ACCEPT_LAWYER, (data: any) => {
      Jetemit.emit(SocketEvents.SEARCH_PREPARATION.ACCEPT_LAWYER, data);
    });

    /* Socket para el cliente para el timer de la busqueda de elaboracion de documentos */
    Socket.on(SocketEvents.SEARCH_PREPARATION.CLIENT_RESPONSE, (data: any) => {
      Jetemit.emit(SocketEvents.SEARCH_PREPARATION.CLIENT_RESPONSE, data);
    });

    Socket.on(SocketEvents.SEARCH_REVISION.ACCEPT_LAWYER, (data: any) => {
      Jetemit.emit(SocketEvents.SEARCH_REVISION.ACCEPT_LAWYER, data);
    });

    Socket.on(SocketEvents.SEARCH_REVISION.CLIENT_RESPONSE, (data: any) => {
      Jetemit.emit(SocketEvents.SEARCH_REVISION.CLIENT_RESPONSE, data);
    });

    /* Evento del cliente para revision documental */
    Socket.on(SocketEvents.REVISION.ASSIGN_LAWYER, (data: any) => {
      Jetemit.emit(SocketEvents.REVISION.ASSIGN_LAWYER, data);
    });

    /* Evento para el abogado de elaboración documental.
       El backend emite este evento cuando un cliente inicia una búsqueda.
       Se maneja aquí de forma global — mismo patrón que REVISION.SEARCH_LAWYER. */
    Socket.on(SocketEvents.SEARCH_PREPARATION.SEARCH_LAWYER, async (data: any) => {
      Jetemit.emit(SocketEvents.SEARCH_PREPARATION.SEARCH_LAWYER, data);
      const currentUser = store.getState().user;
      console.log('[PREP SEARCH_LAWYER] event received', {
        data,
        currentUser_id: currentUser?.id,
        currentUser_level: currentUser?.level_id,
        LAWYER_LEVEL: Constants.LEVELS.LAWYER,
        levelMatch: Number(currentUser?.level_id) === Constants.LEVELS.LAWYER,
        lawyers_ids: data?.lawyers_ids,
        idInList: (data?.lawyers_ids || []).map(Number).includes(Number(currentUser?.id)),
      });
      if (
        Number(currentUser?.level_id) === Constants.LEVELS.LAWYER &&
        Array.isArray(data?.lawyers_ids) &&
        data.lawyers_ids.map(Number).includes(Number(currentUser?.id))
      ) {
        setPendingSearchPreparation(data.search_preparation);
        setVisibleAcceptPreparation(true);
      }
    });

    /* Evento para el abogado de revisión documental.
       Se maneja aquí de forma global para que el modal aparezca
       independientemente de la pantalla en la que esté el abogado. */
    Socket.on(SocketEvents.REVISION.SEARCH_LAWYER, async (data: any) => {
      Jetemit.emit(SocketEvents.REVISION.SEARCH_LAWYER, data);
      const currentUser = store.getState().user;
      console.log('[REVISION SEARCH_LAWYER] event received', {
        data,
        currentUser_id: currentUser?.id,
        currentUser_level: currentUser?.level_id,
        levelMatch: Number(currentUser?.level_id) === Constants.LEVELS.LAWYER,
        user_ids: data?.user_ids,
        idInList: (data?.user_ids || []).map(Number).includes(Number(currentUser?.id)),
      });
      if (
        Number(currentUser?.level_id) === Constants.LEVELS.LAWYER &&
        Array.isArray(data?.user_ids) &&
        data.user_ids.map(Number).includes(Number(currentUser?.id))
      ) {
        try {
          const revision: any = await RevisionService.getOne({
            id: data.revision_id,
            withoutLoading: true,
          });
          setPendingRevision(revision);
          setVisibleAcceptRevision(true);
        } catch (e) {}
      }
    });

    /* DEV-LT-35: evento para el abogado EXTERNO de revisión documental (búsqueda +
       postulación). Distinto del REVISION.SEARCH_LAWYER de arriba, que sigue siendo
       exclusivo del flujo interno (asignación directa). Mirror exacto del listener de
       SEARCH_PREPARATION.SEARCH_LAWYER de más arriba. */
    Socket.on(SocketEvents.SEARCH_REVISION.SEARCH_LAWYER, async (data: any) => {
      Jetemit.emit(SocketEvents.SEARCH_REVISION.SEARCH_LAWYER, data);
      const currentUser = store.getState().user;
      if (
        Number(currentUser?.level_id) === Constants.LEVELS.LAWYER &&
        Array.isArray(data?.lawyers_ids) &&
        data.lawyers_ids.map(Number).includes(Number(currentUser?.id))
      ) {
        setPendingSearchRevision(data.search_revision);
        setVisibleAcceptSearchRevision(true);
      }
    });
  }, []);

  return (
    <React.Fragment>
      {/* Modal global de aceptar elaboración — activo desde cualquier pantalla del abogado */}
      <Modal
        className='modal-accept-revision'
        visible={visibleAcceptPreparation}
        title='Ha recibido una solicitud de elaboración'
        onClose={() => {
          setVisibleAcceptPreparation(false);
          setPendingSearchPreparation(null);
        }}
      >
        <div id='modal-accept-revision'>
          <User
            image={pendingSearchPreparation?.user?.photo}
            title='Cliente'
            name={pendingSearchPreparation?.user?.full_name}
          />
          <p className='type'>
            <span>{pendingSearchPreparation?.area?.name}</span>/{' '}
            {pendingSearchPreparation?.document?.name}
          </p>
          <small>
            {pendingSearchPreparation?.state?.name} - {pendingSearchPreparation?.city?.name}
          </small>
          <small className='notice-text'>
            Puede postularse a esta solicitud. El cliente decide con qué abogado trabajar, según su
            propio criterio — postularse no garantiza que se le asigne el caso.
          </small>
          <div className='container-btn'>
            <button className='btn' onClick={() => acceptPreparationGlobal()}>
              Postularse
            </button>
            <button
              className='btn btn-reject'
              onClick={() => {
                setVisibleAcceptPreparation(false);
                setPendingSearchPreparation(null);
              }}
            >
              Rechazar
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal global de aceptar revisión — activo desde cualquier pantalla del abogado */}
      <Modal
        className='modal-accept-revision'
        visible={visibleAcceptRevision}
        title='Ha recibido una solicitud de revisión'
        onClose={() => {
          setVisibleAcceptRevision(false);
          setPendingRevision(null);
        }}
      >
        <div id='modal-accept-revision'>
          <User
            image={pendingRevision?.user?.photo}
            title='Cliente'
            name={pendingRevision?.user?.full_name}
          />
          <p className='type'>
            <span>{pendingRevision?.area?.name}</span>/ {pendingRevision?.document?.name}
          </p>
          <small>
            {pendingRevision?.user?.state?.name} - {pendingRevision?.user?.city?.name}
          </small>
          <div className='container-btn'>
            <button className='btn' onClick={() => acceptRevisionGlobal()}>
              Aceptar
            </button>
            <button
              className='btn btn-reject'
              onClick={() => {
                setVisibleAcceptRevision(false);
                setPendingRevision(null);
              }}
            >
              Rechazar
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        className='modal-accept-revision'
        visible={visibleAcceptSearchRevision}
        title='Nueva solicitud de Revisión Documental disponible'
        onClose={() => {
          setVisibleAcceptSearchRevision(false);
          setPendingSearchRevision(null);
        }}
      >
        <div id='modal-accept-revision'>
          <User
            image={pendingSearchRevision?.user?.photo}
            title='Cliente'
            name={pendingSearchRevision?.user?.full_name}
          />
          <p className='type'>
            <span>{pendingSearchRevision?.area?.name}</span>/{' '}
            {pendingSearchRevision?.document?.name}
          </p>
          <small className='notice-text'>
            Puede postularse a esta solicitud. El cliente decide con qué abogado trabajar, según su
            propio criterio — postularse no garantiza que se le asigne el caso.
          </small>
          <div className='container-btn'>
            <button className='btn' onClick={() => acceptSearchRevisionGlobal()}>
              Postularse
            </button>
            <button
              className='btn btn-reject'
              onClick={() => {
                setVisibleAcceptSearchRevision(false);
                setPendingSearchRevision(null);
              }}
            >
              Rechazar
            </button>
          </div>
        </div>
      </Modal>

      <Switch>
        {/* Public */}
        {/* <Route exact path="/" component={ConstructionComponent} /> */}
        <Route exact path='/' component={Home} />
        <Route exact path='/memberships' component={RedirectMemberships} />

        <Route exact path='/apacuana/request' component={requestCert} />
        <Route exact path='/apacuana/request/:id' component={requestCert} />
        {/* Auth */}
        <Route exact noAuth path='/login' component={Login} />
        <Route exact auth path='/profile' component={Profile} />
        <Route exact noAuth path='/recover' component={Recover} />
        <Route exact noAuth path='/recover/:code' component={Reset} />
        <Route exact noAuth path='/register/:level_id' component={Register} />

        {/* Admin */}
        <Route exact admin path='/admin' component={Admin} />
        <Route exact admin path='/admin/home' component={AdminHome} />
        <Route
          exact
          admin
          path='/admin/users/moderators'
          component={AdminModerators}
          permission={Constants.MODULES.USERS}
        />
        <Route
          exact
          admin
          path='/admin/users/clients'
          component={AdminClients}
          permission={Constants.MODULES.USERS}
        />
        <Route
          exact
          admin
          path='/admin/users/lawyers'
          component={AdminLawyers}
          permission={Constants.MODULES.USERS}
        />
        <Route
          exact
          admin
          path='/admin/users'
          component={AdminUsers}
          permission={Constants.MODULES.USERS}
        />
        <Route
          exact
          admin
          path='/admin/services'
          component={AdminServices}
          permission={Constants.MODULES.SERVICES}
        />
        <Route
          exact
          admin
          path='/admin/reports'
          component={AdminReports}
          permission={Constants.MODULES.REPORTS}
        />
        <Route
          exact
          admin
          path='/admin/reports/payments'
          component={AdminReportPayments}
          permission={Constants.MODULES.REPORTS}
        />
        <Route
          exact
          admin
          path='/admin/reports/services-payments'
          component={AdminReportServicePayments}
          permission={Constants.MODULES.REPORTS}
        />
        <Route
          exact
          admin
          path='/admin/reports/comissions'
          component={AdminReportComissions}
          permission={Constants.MODULES.REPORTS}
        />
        <Route
          exact
          admin
          path='/admin/reports/package-payments'
          component={AdminReportPackagePayments}
          permission={Constants.MODULES.REPORTS}
        />
        <Route
          exact
          admin
          path='/admin/chat'
          component={AdminChat}
          permission={Constants.MODULES.CHAT}
        />
        <Route
          exact
          admin
          path='/admin/notifications'
          component={AdminNotifications}
          permission={Constants.MODULES.NOTIFICATIONS}
        />
        <Route
          exact
          admin
          path='/admin/calls'
          component={AdminCalls}
          permission={Constants.MODULES.CALLS}
        />
        <Route
          exact
          admin
          path='/admin/formalities'
          component={AdminFormalities}
          permission={Constants.MODULES.FORMALITIES}
        />
        <Route
          exact
          admin
          path='/admin/memberships'
          component={AdminMemberships}
          permission={Constants.MODULES.MEMBERSHIPS}
        />
        <Route
          exact
          admin
          path='/admin/revision'
          component={AdminRevision}
          permission={Constants.MODULES.REVISION}
        />
        <Route
          exact
          admin
          path='/admin/signature'
          component={AdminSignature}
          permission={Constants.MODULES.SIGNATURE}
        />
        <Route
          exact
          admin
          path='/admin/guides'
          component={AdminGuides}
          permission={Constants.MODULES.GUIDES}
        />
        <Route
          exact
          admin
          path='/admin/ratings'
          component={AdminRatings}
          permission={Constants.MODULES.RATINGS}
        />
        <Route
          exact
          admin
          path='/admin/documents'
          component={AdminDocuments}
          permission={Constants.MODULES.DOCUMENTS}
        />
        <Route
          exact
          admin
          path='/admin/preparation'
          component={AdminPreparation}
          permission={Constants.MODULES.PREPARATION}
        />
        <Route exact admin path='/admin/analytics' component={AdminAnalytics} />
        <Route exact admin path='/admin/referrals' component={AdminReferrals} />

        {/* Client */}
        <Route exact client path='/client' component={Client} />
        <Route exact client path='/client/home' component={ClientHome} />
        <Route exact client path='/client/ia-chat' component={ClientBotpressChat} />
        <Route exact client path='/client/chat' component={ClientChat} />
        <Route exact client path='/client/calls' component={ClientCalls} />
        <Route exact client path='/client/formalities' component={ClientFormalities} />
        <Route exact client path='/client/memberships' component={ClientMemberships} />
        <Route exact client path='/client/reports' component={ClientReports} />
        <Route exact client path='/client/revision' component={ClientRevision} />
        <Route exact client path='/client/signatures' component={ClientSignatures} />
        <Route exact client path='/client/surveys' component={ClientSurveys} />
        <Route exact client path='/client/preparation' component={ClientPreparation} />
        <Route exact client path='/client/guides' component={ClientGuides} />
        <Route exact client path='/client/referrals' component={ClientReferrals} />

        {/* Lawyer */}
        <Route exact lawyer path='/lawyer' component={Lawyer} />
        <Route exact lawyer path='/lawyer/home' component={LawyerHome} />
        <Route exact lawyer path='/lawyer/ia-chat' component={ClientBotpressChat} />
        <Route exact path='/lawyer/validation' component={LawyerValidation} />
        <Route exact lawyer path='/lawyer/chat' component={LawyerChat} />
        <Route exact lawyer path='/lawyer/calls' component={LawyerCalls} />
        <Route exact lawyer path='/lawyer/memberships' component={ClientMemberships} />
        <Route exact lawyer path='/lawyer/reports' component={ClientReports} />
        <Route exact lawyer path='/lawyer/revision' component={LawyerRevision} />
        <Route exact lawyer path='/lawyer/signatures' component={LawyerSignatures} />
        <Route exact lawyer path='/lawyer/services' component={LawyerServices} />
        <Route exact lawyer path='/lawyer/preparation' component={LawyerPreparation} />
        <Route exact lawyer path='/lawyer/formalities' component={ClientFormalities} />
        <Route exact lawyer path='/lawyer/guides' component={LawyerGuides} />
        <Route exact lawyer path='/lawyer/referrals' component={ClientReferrals} />

        <Redirect to='/' />
      </Switch>
    </React.Fragment>
  );
};
const ApacuanaGate = ({ children }: PropsWithChildren) => {
  const user = useSelector((state: RootState) => state.user);

  if (!user) {
    return <>{children}</>;
  }

  return <ApacuanaProvider user={user}>{children}</ApacuanaProvider>;
};
const App = () => (
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <Router>
        <ApacuanaGate>
          <Container />
        </ApacuanaGate>
      </Router>
    </PersistGate>
  </Provider>
);

export default App;
